import React, { useEffect, useState, useRef } from 'react';
import AceEditor from 'react-ace';
import ace from 'ace-builds/src-noconflict/ace';
import { 
  Box, 
  Button, 
  Menu, 
  MenuItem, 
  Typography, 
  Chip, 
  Tooltip,
  IconButton,
  Collapse,
  Alert
} from '@mui/material';
import { 
  Code as CodeIcon, 
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useDataProvider } from 'react-admin';
import { getApiUrl } from '../../../framework/config/api.config';

// Configure Ace Editor base path to fix worker loading
ace.config.set('basePath', 'https://unpkg.com/ace-builds@1.32.6/src-noconflict/');
ace.config.set('modePath', 'https://unpkg.com/ace-builds@1.32.6/src-noconflict/');
ace.config.set('workerPath', 'https://unpkg.com/ace-builds@1.32.6/src-noconflict/');
ace.config.set('themePath', 'https://unpkg.com/ace-builds@1.32.6/src-noconflict/');

// Import language modes
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-typescript';
import 'ace-builds/src-noconflict/mode-handlebars';
import 'ace-builds/src-noconflict/mode-sql';

// Import themes
import 'ace-builds/src-noconflict/theme-monokai';

// Import workers for syntax checking
import 'ace-builds/src-noconflict/worker-json';
import 'ace-builds/src-noconflict/worker-javascript';
import 'ace-builds/src-noconflict/worker-css';
import 'ace-builds/src-noconflict/worker-html';

// Import extensions for enhanced features
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ext-searchbox';

interface ContextVariable {
  key: string;
  description: string;
  source: 'tenant' | 'context' | 'system';
  value?: any;
  exampleValue?: string;
}

const TemplateEditorAce = ({  value, onChange, mode = "javascript", annotations = [] }) => {
  // Disable worker for TypeScript mode to avoid validation issues with JSX
  const shouldUseWorker = mode !== 'typescript';
  const dataProvider = useDataProvider();
  const editorRef = useRef<any>(null);
  
  // Local state to manage the editor content as a string
  const [editorValue, setEditorValue] = useState('');
  const [contextVariables, setContextVariables] = useState<Record<string, ContextVariable[]>>({});
  const [showVariables, setShowVariables] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync the editorValue with the prop value when the component mounts or when the prop value changes
  useEffect(() => {
    setEditorValue(value);
  }, [value]);

  // Fetch context variables on mount
  useEffect(() => {
    const fetchContextVariables = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the API configuration to get the correct base URL (handles different ports)
        const apiUrl = getApiUrl('/api/application/context-variables?groupBySource=true');
        console.log('[TemplateEditor] Fetching context variables from:', apiUrl);
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` // Adjust based on your auth setup
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch context variables: ${response.status} ${response.statusText}`);
        }
        
        const variables = await response.json();
        console.log('[TemplateEditor] Loaded context variables:', Object.keys(variables));
        setContextVariables(variables);
        
        // Set up autocomplete for Ace editor
        setupAutocompletion(variables);
      } catch (err) {
        console.error('[TemplateEditor] Error fetching context variables:', err);
        setError('Failed to load context variables');
      } finally {
        setLoading(false);
      }
    };

    fetchContextVariables();
  }, []);

  // Setup Ace autocomplete for {{variable}} syntax
  const setupAutocompletion = (variables: Record<string, ContextVariable[]>) => {
    const allVariables: ContextVariable[] = Object.values(variables).flat();
    
    const customCompleter = {
      getCompletions: (editor: any, session: any, pos: any, prefix: string, callback: any) => {
        // Check if we're inside {{ }}
        const line = session.getLine(pos.row);
        const beforeCursor = line.substring(0, pos.column);
        
        // If we're after {{, suggest variables
        if (beforeCursor.includes('{{')) {
          const suggestions = allVariables.map(variable => ({
            caption: variable.key,
            value: variable.key,
            meta: variable.source,
            score: 1000,
            description: variable.description,
            docHTML: `<div>
              <b>${variable.key}</b><br/>
              <em>${variable.description}</em><br/>
              <small>Source: ${variable.source}</small><br/>
              ${variable.exampleValue ? `<small>Example: ${variable.exampleValue}</small>` : ''}
            </div>`
          }));
          
          callback(null, suggestions);
        } else {
          callback(null, []);
        }
      }
    };

    // Register custom completer
    if (ace.require) {
      const langTools = ace.require('ace/ext/language_tools');
      if (langTools) {
        langTools.addCompleter(customCompleter);
      }
    }
  };

  // Handle change event from AceEditor
  const handleEditorChange = (newValue) => {
    console.log('handleEditorChange', newValue);
    
   // setEditorValue(newValue); // Update local state
   onChange(newValue);
    /*try {
      // Parse the new value to ensure it's valid JSON before calling onChange
      const parsedValue = JSON.parse(newValue);
      onChange(parsedValue); // Call onChange with parsed JSON
    } catch (error) {
      // Do not call onChange if the JSON is invalid
      console.error('Invalid JSON:', error);
    }
      */
  };

  // Insert variable at cursor position
  const insertVariable = (variableKey: string) => {
    if (editorRef.current) {
      const editor = editorRef.current.editor;
      editor.insert(`{{${variableKey}}}`);
      editor.focus();
    }
    setAnchorEl(null);
  };

  // Handle variable menu
  const handleVariableMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleVariableMenuClose = () => {
    setAnchorEl(null);
  };

  const allVariables: ContextVariable[] = Object.values(contextVariables).flat();

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        mb: 1, 
        alignItems: 'center',
        p: 1,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Button
          size="small"
          startIcon={<CodeIcon />}
          onClick={handleVariableMenuClick}
          variant="outlined"
          disabled={loading || allVariables.length === 0}
        >
          Insert Variable ({allVariables.length})
        </Button>
        
        <Tooltip title="Toggle variable reference">
          <IconButton 
            size="small" 
            onClick={() => setShowVariables(!showVariables)}
            color={showVariables ? 'primary' : 'default'}
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>

        {error && (
          <Typography variant="caption" color="error" sx={{ ml: 2 }}>
            {error}
          </Typography>
        )}
      </Box>

      {/* Variable Reference Panel */}
      <Collapse in={showVariables}>
        <Box sx={{ 
          mb: 1, 
          p: 2, 
          bgcolor: 'background.paper', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          <Typography variant="subtitle2" gutterBottom>
            Available Variables
          </Typography>
          <Typography variant="caption" color="text.secondary" paragraph>
            Use these variables in your template with the syntax: <code>{`{{variableName}}`}</code>
          </Typography>
          
          {Object.entries(contextVariables).map(([source, variables]) => (
            <Box key={source} sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'capitalize', color: 'primary.main' }}>
                {source}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {variables.map(variable => (
                  <Tooltip 
                    key={variable.key} 
                    title={`${variable.description}${variable.exampleValue ? ` (e.g., ${variable.exampleValue})` : ''}`}
                  >
                    <Chip 
                      label={variable.key} 
                      size="small" 
                      onClick={() => insertVariable(variable.key)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>

      {/* Variable Insert Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleVariableMenuClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: '350px',
          },
        }}
      >
        {Object.entries(contextVariables).map(([source, variables]) => [
          <MenuItem key={`${source}-header`} disabled>
            <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
              {source} Variables
            </Typography>
          </MenuItem>,
          ...variables.map(variable => (
            <MenuItem 
              key={variable.key} 
              onClick={() => insertVariable(variable.key)}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {`{{${variable.key}}}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {variable.description}
              </Typography>
              {variable.exampleValue && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Example: {variable.exampleValue}
                </Typography>
              )}
            </MenuItem>
          ))
        ]).flat()}
      </Menu>

      {/* Ace Editor */}
      <Box sx={{ flexGrow: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <AceEditor
          ref={editorRef}
          mode={mode}
          theme="monokai"
          value={editorValue}
          onChange={handleEditorChange}
          name="template-editor"
          editorProps={{ $blockScrolling: true }}
          setOptions={{
            useWorker: shouldUseWorker,
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: true,
            showLineNumbers: true,
            tabSize: 2,
            fontSize: 14,
            showPrintMargin: true,
            printMarginColumn: 100,
            highlightActiveLine: true,
            enableMultiselect: true,
            behavioursEnabled: true,
            wrapBehavioursEnabled: true,
            showGutter: true,
            displayIndentGuides: true,
            highlightGutterLine: true,
            showInvisibles: false,
            scrollPastEnd: true,
          }}
          annotations={annotations}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
    </Box>
  );
};


export default TemplateEditorAce;